import React, { createContext, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "bob_admin_token_v1";
const USER_KEY = "bob_admin_user_v1";

const AdminAuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_ROLES = new Set(["owner", "admin", "manager", "staff", "viewer"]);

const isAdminRole = (role) => ADMIN_ROLES.has(String(role || "").toLowerCase());

/**
 * Admin authentication context — holds the JWT and the resolved admin user.
 *
 * Persists the token in localStorage so a hard refresh keeps the admin
 * logged in. The token is the only piece of state we trust client-side; the
 * `user` we render is verified against the backend's `/api/auth/me` on every
 * mount so a stale/expired token is immediately surfaced.
 *
 * Note: handlers below intentionally do NOT use `useCallback` — they only
 * depend on stable state setters, and the React 19 compiler memoizes them
 * for us automatically. Adding manual memoization here trips the
 * `react-hooks/preserve-manual-memoization` rule.
 */
export const AdminAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [user, setUser] = useState(() => {
    try { const raw = localStorage.getItem(USER_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  // `null` = checking, true/false = result of /me probe.
  const [verified, setVerified] = useState(null);

  const doLogout = () => {
    setToken(null);
    setUser(null);
    setVerified(false);
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(USER_KEY);
    } catch (_) { /* ignore */ }
  };

  // Probe /auth/me whenever the token changes so we can detect expired or
  // revoked tokens without waiting for the next protected call.
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      if (!token) { setVerified(false); return; }
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (!isAdminRole(data?.role)) { doLogout(); return; }
          setUser(data);
          try { localStorage.setItem(USER_KEY, JSON.stringify(data)); } catch (_) { /* quota */ }
          setVerified(true);
        } else {
          doLogout();
        }
      } catch (_) {
        if (!cancelled) setVerified(false);
      }
    };
    probe();
    return () => { cancelled = true; };
  }, [token]);

  const login = async (email, password) => {
    const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = data?.detail;
      throw new Error(typeof detail === "string" ? detail : "Login failed");
    }
    if (!isAdminRole(data?.user?.role)) {
      throw new Error("Admin access required");
    }
    setToken(data.token);
    setUser(data.user);
    try {
      localStorage.setItem(STORAGE_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    } catch (_) { /* ignore */ }
    setVerified(true);
    return data.user;
  };

  const logout = async () => {
    if (token) {
      fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    doLogout();
  };

  // Helper: authenticated fetch wrapper used by every admin call.
  const apiFetch = async (path, options = {}) => {
    const headers = { ...(options.headers || {}), Authorization: `Bearer ${token}` };
    // Only set JSON content-type for non-FormData string bodies; FormData
    // must let the browser set its own multipart boundary.
    const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
    if (options.body && !isFormData && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }
    const res = await fetch(`${BACKEND_URL}/api${path}`, { ...options, headers });
    if (res.status === 401) doLogout();
    return res;
  };

  const value = {
    token,
    user,
    verified,
    isAuthenticated: !!token && verified === true,
    login,
    logout,
    apiFetch,
    hasPermission: (permission) => {
      if (!permission) return true;
      const role = String(user?.role || "").toLowerCase();
      if (role === "owner") return true;
      return Array.isArray(user?.permissions) && user.permissions.includes(permission);
    },
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
};
